<template>
  <div class="bookings-calendar-container">
    <FullCalendar
      height="parent"
      slot-duration="00:05:00"
      slot-label-interval="01:00"
      min-time="07:00"
      max-time="21:00"
      :defaultView="defaultView"
      :defaultDate="defaultDate"
      :scroll-time="scrollTime"
      :header="header"
      :plugins="plugins"
      :events="events"
      :now-indicator="true"
      :selectable="true"
      @select="onSelect"
      @eventClick="onEventClick"
      @datesRender="onDatesRender"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import _ from 'lodash';
import moment from 'moment';
import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';

import { paymentStatusTypes } from '@/store/modules/bookings';
import { forceViewValid, forceDateValid } from '@/calendars';

import { eventTypes } from '@/store/modules/roster';

export default {
  components: {
    FullCalendar
  },
  props: {
    resource: String
  },
  data() {
    let { view, date } = this.$route.params;

    const defaultView = forceViewValid(view);
    const defaultDate = forceDateValid(date);
    const scrollTime = moment().format('HH:00:00');

    return {
      header: {
        left: 'prev,next today',
        center: 'title',
        right: 'listDay,timeGridDay,timeGridWeek,dayGridMonth'
      },
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
      defaultView,
      defaultDate,
      scrollTime
    };
  },
  methods: {
    ...mapActions({
      subscribeBookings: 'bookings/subscribeBookings',
      unsubscribeBookings: 'bookings/unsubscribeBookings',
      subscribeRoster: 'roster/subscribeRoster',
      unsubscribeRoster: 'roster/unsubscribeRoster',
      fetchResources: 'resources/fetchResources'
    }),
    onSelect(info) {
      this.$emit('timeslot-click', info);
    },
    onEventClick({ event }) {
      if (this.isRosterEvent(event)) return;
      const booking = this.bookings[event.id];
      this.$emit('booking-click', booking);
    },
    isRosterEvent({ groupId }) {
      return groupId === 'ROSTER';
    },
    getEventTitle(booking) {
      const { resource, service, customer, price } = booking;
      const _package = booking.package;
      return `${customer.name} | ${resource.name} | ${service.name} - ${_package.name} - €${price}`;
    },
    getEventTheme({ paymentStatus }) {
      if (paymentStatus === paymentStatusTypes.NOT_PAID)
        return {
          backgroundColor: 'red',
          textColor: 'white',
          borderColor: 'transparent'
        };

      return {
        backgroundColor: 'blue',
        textColor: 'white',
        borderColor: 'transparent'
      };
    },
    getRosterEventColor({ type }) {
      if (type === eventTypes.RESOURCE_WORK_HOURS) return 'blue';
      if (
        type === eventTypes.RESOURCE_BREAK ||
        type === eventTypes.RESOURCE_HOLIDAY
      )
        return 'orange';
      if (type === eventTypes.RESOURCE_SICK) return 'red';
      return 'grey';
    },
    getRosterEventTitle({ resourceId, type }) {
      const resource = this.resources[resourceId];

      if (!resource) return '';

      let eventTypeTitle;

      if (type === eventTypes.RESOURCE_HOLIDAY) {
        eventTypeTitle = 'Holiday';
      } else if (type === eventTypes.RESOURCE_SICK) {
        eventTypeTitle = 'Sick';
      } else if (type === eventTypes.RESOURCE_BREAK) {
        eventTypeTitle = 'Break';
      }

      return `${resource.name} | ${eventTypeTitle}`;
    },
    onDatesRender({ view }) {
      const { type, currentStart } = view;
      const date = moment(currentStart).format('YYYY-MM-DD');

      this.updateRouteParams({ view: type, date });
    },
    updateRouteParams({ view, date }) {
      const url = this.$route.path.replace(
        /calendar.*/,
        `calendar/${view}/${date}`
      );

      window.history.replaceState(null, '', url);
    }
  },
  computed: {
    ...mapGetters({
      bookings: 'bookings/bookings',
      confirmedBookings: 'bookings/confirmedBookings',
      rosterBreaks: 'roster/rosterBreaks',
      storeId: 'stores/storeId',
      resources: 'resources/resources'
    }),
    filteredBookings() {
      if (!this.resource) return this.confirmedBookings;
      return this.confirmedBookings.filter(
        booking => booking.resource._id === this.resource
      );
    },
    filteredBreaks() {
      const { resource, rosterBreaks } = this;
      if (!resource) return rosterBreaks;
      return rosterBreaks.filter(event => event.resourceId === resource);
    },
    events() {
      const bookingEvents = _.map(this.filteredBookings, booking => {
        const { _id, start, end } = booking;
        return {
          id: _id,
          start,
          end,
          title: this.getEventTitle(booking),
          ...this.getEventTheme(booking)
        };
      });

      const rosterBreakEvents = _.map(this.filteredBreaks, event => {
        const { start, end } = event;
        const color = this.getRosterEventColor(event);
        return {
          start,
          end,
          groupId: 'ROSTER',
          title: this.getRosterEventTitle(event),
          backgroundColor: color,
          textColor: 'white',
          borderColor: 'transparent'
        };
      });

      return [...bookingEvents, ...rosterBreakEvents];
    }
  },
  async created() {
    await this.fetchResources();
    this.subscribeBookings();
    this.subscribeRoster();
  },
  beforeDestroy() {
    this.unsubscribeBookings();
    this.unsubscribeRoster();
  }
};
</script>
